import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
        headers: config.headers,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const appStore = useAppStore();
    const authStore = useAuthStore();

    // Log errors in development
    if (import.meta.env.DEV) {
      console.error('[API Response Error]', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }

    // Handle different error status codes
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          if (authStore.isAuthenticated) {
            try {
              // Try to refresh token
              await authStore.refreshToken();
              // Retry the original request
              if (error.config) {
                return api.request(error.config);
              }
            } catch (refreshError) {
              // Refresh failed, logout user
              await authStore.logout();
              appStore.showError(
                'Sessão expirada',
                'Sua sessão expirou. Faça login novamente.'
              );
            }
          }
          break;

        case 403:
          // Forbidden - insufficient permissions
          appStore.showError(
            'Acesso negado',
            'Você não tem permissão para realizar esta ação.'
          );
          break;

        case 404:
          // Not found
          appStore.showError(
            'Recurso não encontrado',
            'O recurso solicitado não foi encontrado.'
          );
          break;

        case 422:
          // Validation error
          const validationErrors = (data as { errors?: unknown; message?: unknown })?.errors || (data as { message?: unknown })?.message;
          if (validationErrors) {
            if (typeof validationErrors === 'object') {
              // Handle Laravel-style validation errors
              const errorMessages = Object.values(validationErrors).flat();
              appStore.showError(
                'Erro de validação',
                errorMessages.join('\n')
              );
            } else {
              appStore.showError('Erro de validação', String(validationErrors));
            }
          }
          break;

        case 429:
          // Too many requests
          appStore.showError(
            'Muitas tentativas',
            'Você fez muitas tentativas. Tente novamente em alguns minutos.'
          );
          break;

        case 500:
          // Internal server error
          appStore.showError(
            'Erro interno do servidor',
            'Ocorreu um erro interno. Tente novamente mais tarde.'
          );
          break;

        case 503:
          // Service unavailable
          appStore.showError(
            'Serviço indisponível',
            'O serviço está temporariamente indisponível. Tente novamente mais tarde.'
          );
          break;

        default:
          // Generic error
          const message = (data as { message?: string })?.message || 'Ocorreu um erro inesperado';
          appStore.showError('Erro', message);
      }
    } else if (error.request) {
      // Network error
      appStore.showError(
        'Erro de conexão',
        'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.'
      );
    } else {
      // Other error
      appStore.showError('Erro', error.message || 'Ocorreu um erro inesperado');
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiHelpers = {
  // Upload file with progress
  uploadFile: async (
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },

  // Download file
  downloadFile: async (url: string, filename?: string): Promise<void> => {
    try {
      const response = await api.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  },

  // Get with cache
  getWithCache: async (
    url: string,
    config?: AxiosRequestConfig,
    cacheKey?: string,
    cacheDuration = 5 * 60 * 1000 // 5 minutes
  ): Promise<AxiosResponse> => {
    const key = cacheKey || `cache_${url}_${JSON.stringify(config?.params || {})}`;
    const cached = sessionStorage.getItem(key);
    
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < cacheDuration) {
        return { data } as AxiosResponse;
      }
    }

    const response = await api.get(url, config);
    
    // Cache the response
    sessionStorage.setItem(key, JSON.stringify({
      data: response.data,
      timestamp: Date.now(),
    }));

    return response;
  },

  // Clear cache
  clearCache: (pattern?: string): void => {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('cache_') && (!pattern || key.includes(pattern))) {
        sessionStorage.removeItem(key);
      }
    });
  },

  // Batch requests
  batchRequests: async (requests: AxiosRequestConfig[]): Promise<AxiosResponse[]> => {
    return Promise.all(requests.map(config => api.request(config)));
  },

  // Health check
  healthCheck: async (): Promise<boolean> => {
    try {
      await api.get('/health');
      return true;
    } catch (error) {
      return false;
    }
  },
};

// Export types
export type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
};

// Export default api instance
export default api;